import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import swal from 'sweetalert';
import SwitcherService from '../../../services/SwitcherService';
import StreamUnavailable from '../../components/Streams/StreamUnavailable';
import { PageHeader, Card, Button, Badge, Dot } from '../../ui';

const MAX_SLOTS = 8;

/* ---- lightweight HLS thumbnail / preview ---- */
const HlsVideo = ({ src, muted = true, className = '', active = false }) => {
  const ref = useRef(null);
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return undefined;
    let cleanup = () => {};
    let cancelled = false;
    setStatus('loading');
    (async () => {
      const { attachHls } = await import('../../../utils/hlsPlayer');
      if (cancelled) return;
      cleanup = await attachHls(el, src, { onStatusChange: setStatus });
    })();
    return () => { cancelled = true; try { cleanup(); } catch (_) {} };
  }, [src]);
  if (status === 'unavailable') return <StreamUnavailable compact heightClass="tw-h-full" className={className} />;
  return (
    <video
      ref={ref}
      muted={muted}
      playsInline
      className={`tw-h-full tw-w-full tw-bg-black tw-object-contain ${className}`}
      style={active ? { outline: '3px solid #f43f5e', outlineOffset: '-3px' } : undefined}
    />
  );
};

const SwitcherConsole = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [streams, setStreams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const s = await SwitcherService.get(id);
      setState(s);
    } catch (e) {
      setErr(e.response?.data?.error || 'Switcher not found');
    }
  }, [id]);

  useEffect(() => {
    refresh();
    // Sources = the owner's own streams + any team member's — lets a team's
    // camera operators show up as inputs automatically.
    SwitcherService.sources().then(setStreams).catch(() => {});
  }, [refresh]);

  useEffect(() => {
    pollRef.current = setInterval(refresh, 3000);
    return () => clearInterval(pollRef.current);
  }, [refresh]);

  const live = state?.status === 'live';
  const slots = Array.from({ length: MAX_SLOTS }, (_, i) => i + 1);
  const inputBySlot = (n) => (state?.inputs || []).find((x) => x.slot === n);

  const flash = (msg) => { setErr(msg); setTimeout(() => setErr(null), 3500); };

  const take = async (slot) => {
    if (!live || !inputBySlot(slot)) return;
    setState((s) => ({ ...s, active_slot: slot })); // optimistic
    try { await SwitcherService.take(id, slot); }
    catch (e) { flash(e.response?.data?.error || 'Cut failed'); refresh(); }
  };

  const toggleLive = async () => {
    setBusy(true);
    try {
      if (live) await SwitcherService.stop(id);
      else await SwitcherService.start(id);
      await refresh();
    } catch (e) {
      flash(e.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };

  const assignInput = async (slot) => {
    const opts = streams.map((s) => {
      const who = s.owner_label && s.owner_label !== 'You' ? ` — ${s.owner_label}` : '';
      const live = s.is_live ? ' (live)' : '';
      return `<option value="${s.id}">${s.name}${who}${live}</option>`;
    }).join('');
    const wrap = document.createElement('div');
    wrap.innerHTML = `<select class="swal-content__select" style="width:100%;padding:8px;border-radius:8px;">${opts || '<option value="">No streams — create one first</option>'}</select>`;
    // swal takes ownership of the passed node (moves it into its own modal),
    // so `wrap` no longer has it as a child once the modal closes — read
    // .value off this captured reference, not wrap.firstChild, afterward.
    const selectEl = wrap.firstChild;
    const picked = await swal({
      title: `Input ${slot}`,
      text: 'Choose the camera stream for this input',
      content: selectEl,
      buttons: ['Cancel', 'Assign'],
    });
    if (picked === null) return;
    const sel = selectEl.value;
    if (!sel) return;
    const stream = streams.find((s) => String(s.id) === String(sel));
    try {
      await SwitcherService.setInput(id, slot, Number(sel), stream?.name);
      refresh();
    } catch (e) { flash(e.response?.data?.error || 'Failed to assign'); }
  };

  const clearInput = async (slot) => {
    await SwitcherService.removeInput(id, slot).catch(() => {});
    refresh();
  };

  // Push the mixed program onto one of the account's own regular streams —
  // taking over from whatever (failover/VOD) is live there. Reverting sends
  // it back to the switcher's own dedicated Program link and lets that main
  // stream fall back into its normal failover behaviour.
  const pushToMain = async () => {
    if (!live) return flash('Go live first, then push to main');
    setPushBusy(true);
    try {
      const targets = await SwitcherService.pushTargets(id);
      setPushBusy(false);
      if (!targets.length) return flash('No eligible main streams — create one first');
      const opts = targets.map((s) => `<option value="${s.id}">${s.name}${s.is_live ? ' (live)' : ''}</option>`).join('');
      const wrap = document.createElement('div');
      wrap.innerHTML = `<select class="swal-content__select" style="width:100%;padding:8px;border-radius:8px;">${opts}</select>`;
      // Same swal DOM-node-reparenting gotcha as assignInput() above — read
      // .value off this captured reference, not wrap.firstChild, afterward.
      const selectEl = wrap.firstChild;
      const picked = await swal({
        title: 'Push live to main',
        text: 'This will interrupt whatever is currently live (or looping failover) on the chosen stream and replace it with this switcher\'s program.',
        content: selectEl,
        buttons: ['Cancel', 'Push live'],
        dangerMode: true,
      });
      if (!picked) return;
      const sel = Number(selectEl.value);
      if (!sel) return;
      setPushBusy(true);
      await SwitcherService.pushToMain(id, sel);
      await refresh();
    } catch (e) {
      flash(e.response?.data?.error || 'Failed to push to main');
    } finally { setPushBusy(false); }
  };

  const stopPushToMain = async () => {
    setPushBusy(true);
    try {
      await SwitcherService.stopPushToMain(id);
      await refresh();
    } catch (e) {
      flash(e.response?.data?.error || 'Failed to stop pushing to main');
    } finally { setPushBusy(false); }
  };

  if (err && !state) {
    return (
      <div className="tw-mx-auto tw-max-w-lg tw-py-12 tw-text-center">
        <Card><div className="tw-text-content-muted">{err} · <Link to="/switcher" className="tw-text-brand">back</Link></div></Card>
      </div>
    );
  }
  if (!state) return <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>;

  const activeInput = inputBySlot(state.active_slot);

  return (
    <>
      <PageHeader
        title={<span><Link to="/switcher" className="tw-text-content-faint hover:tw-text-content"><i className="fa-solid fa-chevron-left tw-mr-2 tw-text-sm" /></Link>{state.name}</span>}
        subtitle={<>Program: <code className="tw-text-xs">{state.program?.name}</code> · {state.config.width}×{state.config.height} @ {state.config.fps}fps</>}
        actions={
          <>
            <Badge tone={live ? 'live' : 'neutral'} className="tw-self-center">
              {live ? <><Dot tone="live" pulse /> ON AIR</> : 'Idle'}
            </Badge>
            <Button
              variant={live ? 'danger' : 'primary'}
              icon={live ? 'fa-stop' : 'fa-play'}
              onClick={toggleLive}
              disabled={busy}
            >
              {busy ? '…' : live ? 'Stop' : 'Go Live'}
            </Button>
          </>
        }
      />

      {err && <div className="tw-mb-4 tw-rounded-xl tw-bg-live/10 tw-px-4 tw-py-2 tw-text-sm tw-text-live">{err}</div>}

      <div className="tw-grid tw-gap-5 lg:tw-grid-cols-[1.4fr,1fr]">
        {/* Program monitor */}
        <Card noPad className="tw-overflow-hidden">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-live/10 tw-px-4 tw-py-2">
            <span className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-live">Program {activeInput ? `— ${activeInput.label}` : ''}</span>
            {live && (
              state.program?.push_target ? (
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Badge tone="live"><Dot tone="live" pulse /> LIVE on {state.program.push_target.name}</Badge>
                  <Button size="sm" variant="secondary" icon="fa-arrow-rotate-left" onClick={stopPushToMain} disabled={pushBusy}>
                    {pushBusy ? '…' : 'Stop pushing'}
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="primary" icon="fa-satellite-dish" onClick={pushToMain} disabled={pushBusy}>
                  {pushBusy ? '…' : 'Push live to main'}
                </Button>
              )
            )}
          </div>
          <div className="tw-aspect-video tw-bg-black">
            {live && state.program?.hls_url
              ? <HlsVideo src={state.program.hls_url} />
              : <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-content-faint">Program off — press Go Live</div>}
          </div>
          {live && (
            <div className="tw-flex tw-items-center tw-gap-1.5 tw-p-3">
              {slots.map((n) => {
                const inp = inputBySlot(n);
                const on = state.active_slot === n;
                return (
                  <button
                    key={n}
                    onClick={() => take(n)}
                    disabled={!inp}
                    className={`tw-flex tw-h-11 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-text-sm tw-font-extrabold tw-transition ${
                      !inp ? 'tw-bg-sunken tw-text-content-faint tw-cursor-not-allowed'
                        : on ? 'tw-bg-live tw-text-white tw-shadow-glow'
                          : 'tw-bg-raised tw-text-heading hover:tw-bg-brand hover:tw-text-white'
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
              <button
                onClick={() => take(state.active_slot)}
                className="tw-h-11 tw-rounded-lg tw-bg-heading tw-px-4 tw-text-sm tw-font-extrabold tw-text-white"
                title="Re-take current input"
              >
                CUT
              </button>
            </div>
          )}
        </Card>

        {/* Inputs grid */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          {slots.map((n) => {
            const inp = inputBySlot(n);
            const on = live && state.active_slot === n;
            return (
              <div key={n} className={`tw-overflow-hidden tw-rounded-xl tw-bg-ink-900 tw-shadow-card ${on ? 'tw-ring-2 tw-ring-live' : ''}`}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-2.5 tw-py-1.5">
                  <span className="tw-text-xs tw-font-bold tw-text-content-muted">IN {n}</span>
                  {inp ? (
                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-[10px]">
                      {on && <Badge tone="live">ON AIR</Badge>}
                      <Dot tone={inp.camera_live ? 'good' : 'warn'} />
                      <span className="tw-text-content-faint">{inp.camera_live ? 'live' : (inp.mode === 'slate' ? 'no signal' : 'connecting')}</span>
                    </span>
                  ) : <span className="tw-text-[10px] tw-text-content-faint">empty</span>}
                </div>
                <button
                  onClick={() => (inp ? take(n) : assignInput(n))}
                  className="tw-block tw-aspect-video tw-w-full tw-bg-black"
                >
                  {inp && inp.hls_url && inp.camera_live
                    ? <HlsVideo src={inp.hls_url} active={on} />
                    : (
                      <span className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-xs tw-text-content-faint">
                        {inp ? (inp.camera_live ? 'loading…' : 'camera offline') : <><i className="fa-solid fa-plus tw-mr-1" /> add input</>}
                      </span>
                    )}
                </button>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-2.5 tw-py-1.5">
                  <span className="tw-truncate tw-text-xs tw-font-semibold tw-text-heading">{inp ? inp.label : '—'}</span>
                  {inp && (
                    <button onClick={() => (live ? flash('Stop the switcher to change inputs') : clearInput(n))} className="tw-text-content-faint hover:tw-text-live">
                      <i className="fa-solid fa-xmark tw-text-xs" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="tw-mt-4 tw-text-xs tw-text-content-faint">
        Point each camera's encoder at that input stream's RTMP URL. When the switcher is live, click an input (or its number) to cut it to the program output.
        {state.program?.push_target
          ? <> The program is currently pushed live onto <strong>{state.program.push_target.name}</strong> — "Stop pushing" hands that stream back to its normal failover/VOD and returns the program to its own link.</>
          : <> Until you "Push live to main", the program only publishes to its own dedicated link — watch / embed it at <code>{state.program?.hls_url}</code>.</>}
      </p>
    </>
  );
};

export default SwitcherConsole;
